import { AstroResults } from "./Astrology";
import { getIdentityMessage } from "./Identity";
import { Factor, weightToBucket } from "./choices";
import { sumFactorWeights } from "./choices";
import { maxIdentityWeightsSum } from "./Identity";
import { StickFigure, Arrow } from "./Stickfigure";
import { useId } from "react";

interface Rock {
  name: string;
  color: string;
  svg: string;
}

export const rocks: Rock[] = [
  {
    name: "Pebble",
    color: "",
    svg: "",
  },
  {
    name: "Stone",
    color: "",
    svg: "",
  },
  {
    name: "Boulder",
    color: "",
    svg: "",
  },
  {
    name: "Mountain",
    color: "",
    svg: "",
  },
]

export const getRockName = (weight: number, fallback: string = "Rock") => {
  switch (weight) {
    case 1: return "Pebble";
    case 2: return "Stone";
    case 3: return "Boulder";
    case 4: return "Mountain";
    default: return fallback;
  }
};

const rockShapes = [
  "M10,2 Q20,-2 30,3 Q42,5 46,15 Q50,25 45,35 Q40,45 28,48 Q18,50 10,45 Q0,38 2,25 Q0,15 10,2", // 
  "M15,0 Q30,-5 40,10 Q48,20 45,35 Q35,48 18,50 Q5,45 0,30 Q-2,15 15,0", // Replace this, it sucks
  "M12,0 Q25,2 38,12 Q45,25 40,38 Q30,48 15,50 Q5,45 0,35 Q-5,20 12,0", // Pointed top
  "M10,5 Q22,0 35,10 Q48,25 40,40 Q25,50 15,45 Q0,40 2,20 Q0,10 10,5", // Wider bottom
  "M15,2 Q28,0 38,12 Q45,25 38,40 Q25,48 12,45 Q2,35 0,22 Q2,10 15,2" // More egg-shaped
];

export const getRockShape = (burden: number) => {
  return rockShapes[burden - 1];
}

interface RockElemProps {
  rockIndex: number;
  color: string;
  gradientEnd?: string;
  rockSize: number;
}

export const RockElem: React.FC<RockElemProps> = ({ rockIndex, color, gradientEnd, rockSize }) => {
  const gradientId = useId();

  return (
    <svg width={rockSize} height={rockSize} viewBox="0 0 50 50">
      <defs>
        {color && gradientEnd && (
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={gradientEnd} />
          </linearGradient>
        )}
      </defs>
      <path
        d={rockShapes[rockIndex]}
        fill={gradientEnd ? `url(#${gradientId})` : color}
        stroke="#444"
        strokeWidth="0.5"
      />
    </svg>
  )
}

export interface RockContainerProps {
  rockIndex: number;
  defaultLabel: string;
  color: string;
  gradientEnd?: string;
}

export const RockContainer: React.FC<RockContainerProps> = ({ rockIndex, defaultLabel, color, gradientEnd }) => {
  return (
    <div className="rock-container" style={{ flexGrow: 1, display: 'flex', justifyContent: 'left', alignItems: 'start', overflow: 'visible' }}>
      {(() => {
        // Get the rock sizes based on burden level
        const rockSize = 30 * (0.8 + (rockIndex * 0.5));

        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', width: 'auto', height: 'auto' }}>
            <div className="rock-label" style={{ marginBottom: '10px', fontWeight: 'bold' }}>
              {rockIndex === 0 ? defaultLabel : getRockName(rockIndex)}
            </div>
            <div style={{ overflow: 'visible' }}>
              <RockElem rockIndex={rockIndex} color={color} gradientEnd={gradientEnd} rockSize={rockSize} />
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export interface VizContainerProps {
  color: string;
  gradientEnd?: string;
  factors: Factor[];
  maxFactorWeightsSum: number;
  defaultMessage: string;
  defaultLabel: string;
  twoFigs?: boolean;
  getMessage: (weight: number) => string;
}

export const VizInner: React.FC<VizContainerProps> = ({ defaultLabel, color, gradientEnd, factors, maxFactorWeightsSum, defaultMessage, getMessage, twoFigs = false }) => {
  const idx = sumFactorWeights(factors) === 0 ? 0 : weightToBucket(sumFactorWeights(factors), maxFactorWeightsSum);
  return (
    <div className="visualization-inner" style={{ position: 'relative', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', height: 'auto', overflow: 'visible' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '400px', margin: '0 auto', overflow: 'visible' }}>
        <StickFigure />

        {twoFigs && <><Arrow /><StickFigure /></>}

        <RockContainer rockIndex={sumFactorWeights(factors) === 0 ? 0 : idx} color={color} gradientEnd={gradientEnd} defaultLabel={defaultLabel} />
      </div>

      <p className="message-box" style={{ marginTop: '20px', width: '100%', maxWidth: '600px' }}>
        {sumFactorWeights(factors) === 0 ? defaultMessage : getMessage(idx)}
      </p>
    </div>
  );
}